import React, { useCallback, useEffect, useRef, useState } from 'react'
import propTypes from 'prop-types'
import classNames from 'classnames/bind'

import AspectRatio from 'components/ui/AspectRatio'
import ViewportEnter from 'components/motion/ViewportEnter'
import WistiaEmbed from 'components/ui/WistiaEmbed'

import s from './HeroInlineVideoSection.module.scss'
const cn = classNames.bind(s)

// Figma video: 347×195 @ 375 mobile, 475×267 @ 1440 desktop
const FIGMA_VIDEO_RATIO_MOBILE = 347 / 195
const FIGMA_VIDEO_RATIO_DESKTOP = 475 / 267

const HeroInlineVideoMedia = ({ videoEmbedID, isPageTransitioning = false }) => {
  const id = (videoEmbedID || '').trim()
  const [isRevealed, setIsRevealed] = useState(false)
  const [isInView, setIsInView] = useState(false)
  const videoRef = useRef(null)
  const isInViewRef = useRef(false)
  const isPageTransitioningRef = useRef(isPageTransitioning)

  useEffect(() => {
    isPageTransitioningRef.current = isPageTransitioning
  }, [isPageTransitioning])

  useEffect(() => {
    setIsRevealed(false)
    setIsInView(false)
    videoRef.current = null
    isInViewRef.current = false
  }, [id])

  const syncPlayback = useCallback((shouldPlay) => {
    const video = videoRef.current
    if (!video?.state || !video?.play || !video?.pause) return

    const state = video.state()
    if (shouldPlay && state === 'paused') video.play()
    if (!shouldPlay && state === 'playing') video.pause()
  }, [])

  const onViewportEnter = useCallback(() => {
    isInViewRef.current = true
    setIsInView(true)
    syncPlayback(true)
  }, [syncPlayback])

  const onViewportExit = useCallback(() => {
    isInViewRef.current = false
    setIsInView(false)
    syncPlayback(false)
  }, [syncPlayback])

  const onVideoReady = useCallback(
    (video) => {
      videoRef.current = video

      const reveal = () => {
        if (isPageTransitioningRef.current) return
        setIsRevealed(true)
      }

      video.bind?.('play', reveal)

      // Belt-and-suspenders loop — endVideoBehavior alone can stall on last frame
      const restart = () => {
        if (!isInViewRef.current) return
        video.time?.(0)
        video.play?.()
      }
      video.bind?.('end', restart)

      video.mute?.()

      if (video.state?.() === 'playing') {
        reveal()
      }

      syncPlayback(isInViewRef.current)
    },
    [syncPlayback],
  )

  if (!id) return null

  return (
    <ViewportEnter onEnter={onViewportEnter} onExit={onViewportExit} once={false}>
      <div className={cn('mediaViewport')}>
        <AspectRatio
          ratio={FIGMA_VIDEO_RATIO_MOBILE}
          desktopRatio={FIGMA_VIDEO_RATIO_DESKTOP}
          className={cn('media', { isRevealed })}
        >
          <div className={cn('mediaOuter')}>
            <div className={cn('mediaInner')}>
              <WistiaEmbed
                key={id}
                id={id}
                remountOnRouteChange={false}
                controls={false}
                initialState={isInView ? 'playing' : 'paused'}
                readyCallback={onVideoReady}
                options={{
                  autoPlay: true,
                  muted: true,
                  silentAutoPlay: true,
                  endVideoBehavior: 'loop',
                  resumable: false,
                  videoFoam: true,
                  controlsVisibleOnLoad: false,
                  playButton: false,
                }}
              />
            </div>
          </div>
        </AspectRatio>
      </div>
    </ViewportEnter>
  )
}

HeroInlineVideoMedia.propTypes = {
  videoEmbedID: propTypes.string,
  isPageTransitioning: propTypes.bool,
}

export default HeroInlineVideoMedia
